import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminSuppliersKeys } from './admin-suppliers.queries';
import { loaders } from './admin-suppliers.loaders';

export const useAdminCreateSupplier = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.createSupplier, {
    onSuccess: () => {
      return queryClient.invalidateQueries(adminSuppliersKeys.lists);
    },
  });
};

export const useAdminUpdateSupplier = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.updateSupplier, {
    onSuccess: () => {
      return queryClient.invalidateQueries(adminSuppliersKeys.all);
    },
  });
};

export const useAdminDeleteSupplier = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.deleteSupplier, {
    onSuccess: () => {
      return queryClient.invalidateQueries(adminSuppliersKeys.lists);
    },
  });
};

export const useAdminEnablePartnerPortal = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.enablePartnerPortal, {
    onSuccess: (_data, { supplierId }) => {
      return queryClient.invalidateQueries(
        adminSuppliersKeys.detail(supplierId),
      );
    },
  });
};

export const useAdminOpenPartnerPortal = () => {
  return useMutation(loaders.openPartnerPortal);
};

export const useAdminDisablePartnerPortal = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.disablePartnerPortal, {
    onSuccess: (_data, { supplierId }) => {
      return queryClient.invalidateQueries(
        adminSuppliersKeys.detail(supplierId),
      );
    },
  });
};

export const useAdminCreatePartnerAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.createPartnerAdmin, {
    onSuccess: (_data, { supplierId }) => {
      return queryClient.invalidateQueries(
        adminSuppliersKeys.detail(supplierId, 'admins'),
      );
    },
  });
};

export const useAdminUpdatePartnerAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.updatePartnerAdmin, {
    onSuccess: (_data, { supplierId }) => {
      return queryClient.invalidateQueries(
        adminSuppliersKeys.detail(supplierId, 'admins'),
      );
    },
  });
};

export const useAdminDeletePartnerAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.deletePartnerAdmin, {
    onSuccess: (_data, { supplierId }) => {
      return queryClient.invalidateQueries(
        adminSuppliersKeys.detail(supplierId, 'admins'),
      );
    },
  });
};

const invalidatePartnerAdmin = (
  queryClient: ReturnType<typeof useQueryClient>,
  supplierId: string,
) => {
  return queryClient.invalidateQueries(
    adminSuppliersKeys.detail(supplierId, 'admins'),
  );
};

export const useAdminLinkPartnerAdminCognito = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.linkPartnerAdminCognito, {
    onSuccess: (_data, { supplierId }) => {
      return invalidatePartnerAdmin(queryClient, supplierId);
    },
  });
};

export const useAdminCreatePartnerAdminCognito = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.createPartnerAdminCognito, {
    onSuccess: (_data, { supplierId }) => {
      return invalidatePartnerAdmin(queryClient, supplierId);
    },
  });
};

const invalidateEarningPeriods = (
  queryClient: ReturnType<typeof useQueryClient>,
  supplierId: string,
) => {
  return queryClient.invalidateQueries(
    adminSuppliersKeys.detail(supplierId, 'earningPeriods'),
  );
};

export const useAdminOpenEarningPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.openEarningPeriod, {
    onSuccess: (_data, { supplierId }) => {
      return invalidateEarningPeriods(queryClient, supplierId);
    },
  });
};

export const useAdminCloseEarningPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.closeEarningPeriod, {
    onSuccess: (_data, { supplierId }) => {
      return invalidateEarningPeriods(queryClient, supplierId);
    },
  });
};
